// The loader of the in-browser event classifier — one per client.
//
// The model weights (~640KB) are a static asset, so loading them is a
// host concern (fetch). The SDK stays pure: a client's `events.classifier`
// starts unconfigured, the host wires its byte loader once at boot
// (`configure`), and the model is fetched + parsed lazily on first use and
// cached on that instance. UI code calls `suggestOpt` per title — it
// returns null until the model is ready (kicking off the load) and the real
// suggestion thereafter. Nothing here is process-global: two clients load
// (and cache) their models independently.

import type { Option } from "@tento-lona/sheets";
import { Tracing } from "@tento-core/tracing";

import type { EventCategory } from "../events/event-category-item.ts";
import { EventClassifier } from "./model.ts";

/**
 * Lazily loads and caches one `EventClassifier` from a byte loader — what
 * `client.events.classifier` is. Build one directly (`new
 * EventClassifierLoader(bytes)`) for a classifier outside any client (a
 * component gallery, a test).
 */
export class EventClassifierLoader {
  #loader: Option<EventClassifierLoader.Bytes>;
  #loaded: Option<EventClassifier> = null;
  #loading: Option<Promise<Option<EventClassifier>>> = null;

  /** `loader`: the model-bytes loader; `null` until `configure` wires one. */
  constructor(loader: Option<EventClassifierLoader.Bytes>) {
    this.#loader = loader;
  }

  /**
   * Wire (or replace) the model-bytes loader — the host does this once at
   * boot. A model already loaded stays cached.
   */
  configure(loader: EventClassifierLoader.Bytes): void {
    this.#loader = loader;
  }

  /**
   * Lazily fetch + parse + cache the classifier. Resolves to null if no
   * loader is configured or the load fails (a failure clears the latch
   * so a later call can retry).
   */
  ensure(): Promise<Option<EventClassifier>> {
    if (this.#loaded) return Promise.resolve(this.#loaded);
    if (this.#loading) return this.#loading;
    const loader = this.#loader;
    if (!loader) return Promise.resolve(null);
    const loading = loader()
      .then((bytes) => {
        const classifier = EventClassifier.fromBytes(bytes);
        this.#loaded = classifier;
        Tracing.info("ui")("event classifier loaded", {
          revision: classifier.modelRevision,
        });
        return classifier;
      })
      .catch((e) => {
        Tracing.warn("ui")("event classifier failed to load", e);
        this.#loading = null;
        return null;
      });
    this.#loading = loading;
    return loading;
  }

  /**
   * The already-loaded classifier, or null. A null result kicks off a
   * background load so a subsequent call can succeed synchronously.
   */
  current(): Option<EventClassifier> {
    if (this.#loaded) return this.#loaded;
    void this.ensure();
    return null;
  }

  /**
   * Best category for a title, or null when the model isn't loaded yet
   * or the title is empty. Safe to call on every keystroke.
   */
  classifyOpt(title: string): Option<EventCategory> {
    return this.current()?.classify(title) ?? null;
  }

  /**
   * Like {@link classifyOpt} but abstains (returns null) when the top
   * score is below `minConfidence` (`null`: `DEFAULT_MIN_CONFIDENCE`). Use for live UI hints so weak,
   * likely-wrong predictions don't show a misleading chip. Also
   * abstains on empty titles / an unloaded model.
   *
   * The 0.35 default was tuned against the effectiveness fixture set:
   * scores are per-class independent sigmoids (not a softmax), so
   * short single-token titles ("dance", "work out", "running") land in
   * the 0.35–0.5 band even when correct. At 0.35 the fixture set shows
   * 98% of correct predictions with zero wrong chips; 0.5 needlessly
   * silenced correct-but-modest suggestions.
   */
  suggestOpt(
    title: string,
    minConfidence: Option<number>,
  ): Option<EventCategory> {
    if (title.trim().length === 0) return null;
    const c = this.current();
    if (!c) return null;
    const top = c.classifyWithScores(title)[0];
    const threshold = minConfidence ?? EventClassifierLoader.DEFAULT_MIN_CONFIDENCE;
    return top && top[1] >= threshold ? top[0] : null;
  }
}

/** Types and values that go with `EventClassifierLoader`: `Bytes`, `DEFAULT_MIN_CONFIDENCE`. */
export namespace EventClassifierLoader {
  /** Fetches the trained model's `.bin` weight bytes. */
  export type Bytes = () => Promise<Uint8Array>;

  /** `suggestOpt`'s default abstain threshold (see there). */
  export const DEFAULT_MIN_CONFIDENCE = 0.35;
}
